/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import * as React from "react";

import Typography from '@material-ui/core/Typography';
import classNames from "classnames";

import { useStyles } from "./style";
import { useIntl } from "react-intl";

interface Props {
    label: string,
    alignCenter?: boolean,
}

function InactiveStatus({ label, alignCenter }: Props) {
  const classes = useStyles();
  return (
    <div
      className={classNames(
        classes.statusContainer,
        "inactive",
        alignCenter && classes.statusLabel
      )}
    >
      <span className={classNames(classes.dot, classes.stoppedDot)} />
      <Typography className={classes.statusText}>
        {label}
      </Typography>
    </div>
  );
}

function ActiveStatus(props: Props) {
    const { label, alignCenter } = props;
    const classes = useStyles();
    return (
        <div className={classNames(classes.statusContainer, "active", alignCenter && classes.statusLabel)}>
            <span className={classNames(classes.dot, classes.activeDot)} />
            <Typography className={classes.statusText}>
                {label}
            </Typography>
        </div>
    );
}

function UnknownStatus(props: Props) {
    const { label } = props;
    const classes = useStyles();
    return (
        <div className={classes.statusContainer}>
            <span className={classNames(classes.dot, classes.draftedDot)} />
            <Typography className={classes.statusText}>
                {label}
            </Typography>
        </div>
    );
}

function PendingStatus({ label }: Props) {
  const classes = useStyles();
  return (
    <div className={classes.statusContainer}>
      <span className={classNames(classes.dot, classes.pendingDot)} />
      <Typography className={classes.statusText}>
        {label}
      </Typography>
    </div>
  );
}

export function Status({
  type,
  alignCenter,
}: {
  type: string | undefined;
  alignCenter?: boolean;
}) {
  const statusType = type?.replace(/_/g, " ");

  const intl = useIntl();
  const statusMessages: { [key: string]: string } = {
    approved: intl.formatMessage({ id: 'status.approved', defaultMessage: 'APPROVED' }),
    unblocked: intl.formatMessage({ id: 'status.active', defaultMessage: 'Active' }),
    blocked: intl.formatMessage({ id: 'status.blocked', defaultMessage: 'Blocked' }),
    rejected: intl.formatMessage({ id: 'status.rejected', defaultMessage: 'Rejected' }),
    "prod only blocked": intl.formatMessage({ id: 'status.prodOnlyBlocked', defaultMessage: 'Prod Only Blocked' }),
    "tier update pending": intl.formatMessage({ id: 'status.pendingUpdate', defaultMessage: 'Pending Update' }),
    "on hold": intl.formatMessage({ id: 'status.pendingCreation', defaultMessage: 'Pending Creation' }),
  };

  const defaultLabel = "Unknown Status";

  const label = statusType
    ? statusMessages[statusType] || defaultLabel
    : defaultLabel;

  return (
    <React.Fragment>
      {(statusType === "approved" || statusType === "unblocked") && (
        <ActiveStatus label={label} alignCenter={alignCenter} />
      )}
      {(statusType === "blocked" ||
        statusType === "rejected" ||
        statusType === "prod only blocked") && (
        <InactiveStatus label={label} alignCenter={alignCenter} />
      )}
      {statusType === "tier update pending" && <PendingStatus label={label} />}
      {statusType === "on hold" && <UnknownStatus label={label} />}
    </React.Fragment>
  );
}
